import { createContext, useContext, useLayoutEffect, useRef } from "react"

import { Box, Fade, SvgIcon as MUISvgIcon, Slide, Typography } from "@mui/material"
import { InView } from "react-intersection-observer"

import { gsap } from "@ggs/ui/local/gsap"
import type { LinkType } from "../../graphql/types"
import { Button, Container, WYSIWYG, type FC, type FCWC } from ".."

type Layout = "full" | "centered" | "centered_line" | "centered_with_svg"

type TextOverviewProps = {
  title?: string
  description: string
  headline?: string
  subtitle?: string
  layout?: Layout
  link?: LinkType
}

const TextOverviewContext = createContext<{
  layout: Layout
}>({
  layout: "full",
})

const TextOverview: FC<TextOverviewProps> = ({
  layout = "full",
  title,
  subtitle,
  headline,
  description,
  link,
}) => {
  const showAnimatedSvg = layout === "full" || layout === "centered_with_svg"
  return (
    <TextOverviewContext.Provider value={{ layout }}>
      <Box position="relative" overflow="hidden" sx={{ isolation: "isolate" }}>
        <TextOverviewContainer>
          {subtitle && <TextOverviewSubtitle>{subtitle}</TextOverviewSubtitle>}
          {title && <TextOverviewTitle>{title}</TextOverviewTitle>}
          {headline && <TextOverviewHeadline>{headline}</TextOverviewHeadline>}
          {layout === "full" ? (
            <WYSIWYG content={description} />
          ) : (
            <TextOverviewContent>
              <WYSIWYG content={description} />
            </TextOverviewContent>
          )}

          {link?.title && <TextOverviewLink {...{ link }} />}
          {layout === "centered_line" && <StraightSVGLine />}
        </TextOverviewContainer>
        {showAnimatedSvg && <AnimatedSvgIcon />}
      </Box>
    </TextOverviewContext.Provider>
  )
}
export default TextOverview

/** A sub-component for the text overview container  */
const TextOverviewContainer: FCWC = ({ children }) => {
  const { layout } = useContext(TextOverviewContext)

  return (
    <Container
      py={{ xs: 10, md: 24 }}
      position="relative"
      zIndex={1}
      sx={(theme) => ({
        ...(layout === "centered_with_svg" && {
          pb: { xs: 0, md: 0 },
        }),
      })}
    >
      <Box
        sx={{
          maxWidth: { md: layout === "full" ? 876 : 1000 },
          ...(layout === "full" && {
            ml: { xl: 32 },
            textAlign: "left",
          }),
          ...(layout !== "full" && {
            mx: "auto",
            textAlign: "center",
          }),
          ...(layout === "centered_with_svg" && {
            maxWidth: 632,
          }),
        }}
      >
        {children}
      </Box>
    </Container>
  )
}

/** A sub-component for the text overview pretitle (subtitle)  */
const TextOverviewSubtitle: FCWC = ({ children }) => (
  <Typography component="h3" variant="body.lg" mb={{ xs: 7, md: 10 }}>
    {children}
  </Typography>
)

/** A sub-component for the text overview title  */
const TextOverviewTitle: FCWC = ({ children }) => {
  const { layout } = useContext(TextOverviewContext)
  return (
    <Typography
      component="h2"
      variant={layout === "centered" ? "heading.xl" : "heading.2xl"}
      mb={{ xs: 12, md: 15 }}
      sx={{
        ...(layout === "centered_with_svg" && {
          fontSize: "52px",
          mb: { xs: 4, md: 6 },
        }),
      }}
    >
      {children}
    </Typography>
  )
}

/** A sub-component for the text overview title  */
const TextOverviewHeadline: FCWC = ({ children }) => (
  <Typography component="h3" variant="heading.sm" mb={{ xs: 7, md: 10 }}>
    {children}
  </Typography>
)

/** A sub-component for the text overview content  */
const TextOverviewContent: FCWC = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  return (
    <Box ref={containerRef}>
      <InView triggerOnce>
        {({ inView, ref }) => (
          <Box ref={ref}>
            <Slide direction="up" in={inView} container={containerRef.current} timeout={350}>
              <Fade in={inView} timeout={350}>
                <Box>{children}</Box>
              </Fade>
            </Slide>
          </Box>
        )}
      </InView>
    </Box>
  )
}

/** A sub-component for the text overview link  */
type LinkProps = { link: LinkType }
const TextOverviewLink: FC<LinkProps> = ({ link }) => (
  <Box mt={{ xs: 12, md: 15 }}>
    <Button variant="default" href={link.uri} label={link.title} />
  </Box>
)

/** A sub-component for the animated svg in the bg  */
const AnimatedSvgIcon = () => {
  const mainRef = useRef<HTMLDivElement>(null)
  const pathRef = useRef<SVGPathElement>(null)
  const pointRef = useRef<SVGGElement>(null)
  const { layout } = useContext(TextOverviewContext)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(pathRef.current, {
        strokeDashoffset: 848,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top 75%",
          end: "bottom center",
          scrub: true,
          // markers: true,
        },
      })
      gsap.to(pointRef.current, {
        opacity: 1,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "bottom center",
          end: "+=100",
          scrub: true,
          // markers: true,
        },
      })
    }, mainRef)
    return () => ctx.revert()
  }, [mainRef, pathRef, pointRef])

  return (
    <Box
      ref={mainRef}
      sx={(theme) => ({
        width: 836,
        height: 207,
        position: "absolute",
        top: 163,
        display: { xs: "none", xl: "block" },
        zIndex: 0,

        [theme.breakpoints.down(1600)]: {
          right: -437,
        },
        [theme.breakpoints.between(1600, 2035)]: {
          left: 1199,
        },
        [theme.breakpoints.up(2035)]: {
          right: 0,
        },

        ...(layout === "centered_with_svg" && {
          top: 20,
        }),
      })}
    >
      <MUISvgIcon
        viewBox="0 0 830 208"
        sx={{
          width: 830,
          height: 208,
        }}
      >
        <path
          fill="none"
          stroke="#F05B2A"
          strokeDasharray=".1 10"
          strokeLinecap="round"
          strokeWidth="2"
          d="M828.83 45.367C785.225 29.412 741.933 5.495 695.441 2.375 631.754-1.9 585.635 27.83 543.083 60.646c-30.253 23.33-62.846 44.691-94.447 66.708-40.774 28.409-81.521 56.592-134.048 69.847-52.768 13.315-111.636 9.665-168.236 3.141-32.471-3.742-70.003-11.736-101.17-23.139"
        />
        <path
          ref={pathRef}
          fill="none"
          stroke="#fff"
          strokeWidth="3"
          strokeDasharray={848}
          strokeDashoffset={848 * 2} // should start doubled to reveal the whole line from right to left
          d="M828.83 45.367C785.225 29.412 741.933 5.495 695.441 2.375 631.754-1.9 585.635 27.83 543.083 60.646c-30.253 23.33-62.846 44.691-94.447 66.708-40.774 28.409-81.521 56.592-134.048 69.847-52.768 13.315-111.636 9.665-168.236 3.141-32.471-3.742-70.003-11.736-101.17-23.139"
        />
        <g ref={pointRef} opacity="0">
          <path
            d="M53.2705 173.306C48.8573 185.805 35.1476 192.359 22.649 187.946C10.1505 183.533 3.59599 169.823 8.00919 157.325C12.4224 144.826 26.1321 138.272 38.6307 142.685C51.1292 147.098 57.6837 160.808 53.2705 173.306Z"
            fill="#FDEEE9"
            fillOpacity="0.4"
          />
          <path
            d="M34.4116 166.647C33.6761 168.731 31.3911 169.823 29.3081 169.087C27.225 168.352 26.1325 166.067 26.8681 163.984C27.6036 161.901 29.8886 160.808 31.9717 161.544C34.0548 162.279 35.1472 164.564 34.4116 166.647Z"
            fill="#F05B2A"
          />
        </g>
      </MUISvgIcon>
    </Box>
  )
}

const StraightSVGLine = () => {
  const mainRef = useRef<HTMLDivElement>(null)
  const pathRef = useRef<SVGLineElement>(null)
  const pointRef = useRef<SVGGElement>(null)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(pathRef.current, {
        attr: {
          y1: 143,
        },
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top 80%",
          end: "bottom 80%",
          scrub: true,
          // markers: true,
        },
      })
      gsap.to(pointRef.current, {
        opacity: 1,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "bottom 80%",
          end: "+=100",
          scrub: true,
          // markers: true,
        },
      })
    }, mainRef)
    return () => ctx.revert()
  }, [mainRef, pathRef, pointRef])

  return (
    <Box ref={mainRef} textAlign="center" mt={25} mb={-15} display={{ xs: "none", md: "block" }}>
      <MUISvgIcon
        viewBox="0 0 66 202"
        sx={{
          width: 66,
          height: 202,
        }}
      >
        <line
          ref={pathRef}
          x1="31.9998"
          y1="0"
          x2="31.9998"
          y2="1"
          stroke="#F05B2A"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="0.1 10"
        />
        <g ref={pointRef} opacity="0">
          <circle
            cx="32.3601"
            cy="170.765"
            r="24"
            transform="rotate(180 32.3601 170.765)"
            fill="#FDEEE9"
            fillOpacity="0.4"
          />
          <circle
            cx="32.103"
            cy="171.104"
            r="4"
            transform="rotate(180 32.103 171.104)"
            fill="#F05B2A"
          />
        </g>
      </MUISvgIcon>
    </Box>
  )
}

const StraightSVGLine2 = () => {
  const mainRef = useRef<HTMLDivElement>(null)
  const pathRef = useRef<SVGPathElement>(null)
  const pointRef = useRef<SVGGElement>(null)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(pathRef.current, {
        strokeDashoffset: 0,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top 75%",
          end: "bottom center",
          scrub: true,
          // markers: true,
        },
      })
      gsap.to(pointRef.current, {
        opacity: 1,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "bottom center",
          end: "+=100",
          scrub: true,
          // markers: true,
        },
      })
    }, mainRef)
    return () => ctx.revert()
  }, [mainRef, pathRef, pointRef])

  return (
    <Box ref={mainRef} textAlign="center" mt={25} display={{ xs: "none", md: "block" }}>
      <MUISvgIcon
        viewBox="0 0 49 195"
        sx={{
          width: 49,
          height: 195,
        }}
      >
        <path
          ref={pathRef}
          d="M25 144a.5.5 0 0 1-.5-.5V.5a.5.5 0 0 1 1 0v143a.5.5 0 0 1-.5.5Z"
          clipRule="evenodd"
          fillRule="evenodd"
          fill="none"
          stroke="#F05B2A"
          strokeWidth="2"
          strokeDasharray="290"
          strokeDashoffset="290"
          transform="rotate(180deg)"
        />
        <path
          d="M25 144a.5.5 0 0 1-.5-.5V.5a.5.5 0 0 1 1 0v143a.5.5 0 0 1-.5.5Z"
          clipRule="evenodd"
          fillRule="evenodd"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="5"
          strokeDasharray="4 5"
          transform="rotate(180deg)"
        />
        <g ref={pointRef} opacity="0">
          <path
            fill="#FDEEE9"
            fillOpacity=".4"
            d="M.36 170.765c0-13.255 10.745-24 24-24s24 10.745 24 24-10.745 24-24 24-24-10.745-24-24Z"
          />
          <path fill="#F05B2A" d="M20.103 171.104a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z" />
        </g>
      </MUISvgIcon>
    </Box>
  )
}
